
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Mail } from "lucide-react";
import { Contact } from "@/types/investor";
import { CustomEmail } from "./ContactSelector";

interface EmailConfigSectionProps {
  isLoading: boolean;
  filteredContacts: Contact[];
  customEmails: CustomEmail[];
  selectedContact: string;
  onContactChange: (contact: string) => void;
  onGenerateEmail: () => void;
  onAddCustomEmail: (name: string, email: string) => void;
}

const EmailConfigSection = ({
  isLoading,
  filteredContacts,
  customEmails,
  selectedContact,
  onContactChange,
  onGenerateEmail,
  onAddCustomEmail
}: EmailConfigSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newCustomName, setNewCustomName] = useState("");
  const [newCustomEmail, setNewCustomEmail] = useState("");

  const handleAddCustomEmail = () => {
    if (!newCustomName.trim() || !newCustomEmail.trim()) {
      toast.error("Name and email are required");
      return;
    }

    if (!isValidEmail(newCustomEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    onAddCustomEmail(newCustomName, newCustomEmail);
    
    // Clear form
    setNewCustomName("");
    setNewCustomEmail("");
    setIsDialogOpen(false);
    
    toast.success("Custom email added");
  };
  
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div>
      <h3 className="text-lg font-medium mb-3">Configuration</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Contact
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Select 
                value={selectedContact} 
                onValueChange={onContactChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a contact" />
                </SelectTrigger>
                <SelectContent>
                  {filteredContacts.length > 0 && (
                    <SelectItem value="group-contacts" disabled>
                      Available Contacts
                    </SelectItem>
                  )}
                  
                  {filteredContacts.length > 0 ? (
                    filteredContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name || "Unnamed"} - {contact.role || "No role"}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No contacts found</SelectItem>
                  )}
                  
                  {customEmails.length > 0 && (
                    <SelectItem value="group-custom" disabled>
                      Custom Emails
                    </SelectItem>
                  )}
                  
                  {customEmails.map(custom => (
                    <SelectItem key={custom.id} value={custom.id}>
                      {custom.name} - {custom.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <UserPlus className="h-4 w-4 mr-1" />
                  Add Email
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Custom Email</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Name</label>
                    <Input 
                      value={newCustomName}
                      onChange={(e) => setNewCustomName(e.target.value)}
                      placeholder="Contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input 
                      value={newCustomEmail}
                      onChange={(e) => setNewCustomEmail(e.target.value)}
                      placeholder="email@example.com"
                      type="email"
                    />
                  </div>
                  <Button 
                    onClick={handleAddCustomEmail} 
                    className="w-full"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Add Custom Email
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            This contact's information will be used in the forwardable email
          </p>
        </div>

        <Button 
          onClick={onGenerateEmail}
          disabled={isLoading || !selectedContact} 
          className="w-full bg-brand-600 hover:bg-brand-700"
        >
          {isLoading ? "Generating..." : "Generate Forwardable Email"}
        </Button>
      </div>
    </div>
  );
};

export default EmailConfigSection;
